import { APP_STORE_URL, AnalyticsEventName } from '@dupe/common/constants'
import { getShopKey } from '@dupe/common/helpers'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import {
  isChrome,
  isChromium,
  isFirefox,
  isIOS,
  isMobile,
  isSafari,
} from 'react-device-detect'
import Button from '~/src/components/Button'
import LottiePlayer from '~/src/components/LottiePlayer'
import PhoneCaptureForm, { InputData } from '~/src/components/PhoneCaptureForm'
import AppleIcon from '~/src/components/svg/AppleIcon'
import CurvedArrow from '~/src/components/svg/CurvedArrow'
import ReloadIcon from '~/src/components/svg/ReloadIcon'
import { useAppContext } from '~/src/context/AppProvider'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import useAnalytics from '~/src/hooks/useAnalytics'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useGetUrlFromHash from '~/src/hooks/useGetUrlFromHash'
import useSubmitIssue from '~/src/hooks/useSubmitIssue'

interface Props {
  resultsData?: ResultsResp
  error?: string
}

const ResultsErrorForm: React.FC<Props> = ({ resultsData, error }) => {
  const analytics = useAnalytics()
  const router = useRouter()
  const { hash } = useAppContext()
  const { tempUserId } = useGetTempUserId()
  const { url: urlFromHash } = useGetUrlFromHash()
  const { submit, processing, submitted, error: submitError } = useSubmitIssue()
  const [reloading, setReloading] = useState(false)
  const [eventSent, setEventSent] = useState(false)

  const urlParam = decodeURIComponent((router.query.url || '') as string)
  const url = urlParam || resultsData?.url || urlFromHash
  const shopKey = resultsData?.shopKey || getShopKey(url)
  const image = resultsData?.image || resultsData?.dataDetected?.image

  const submitIssue = useCallback(
    async (data?: InputData) => {
      if (!hash) return
      if (!tempUserId) return
      if (processing) return
      if (submitted) return
      if (submitError) return

      const metadata = {
        hash,
        url,
        urlParam,
        urlFromHash,
        shopKey,
        image,
        error,
        ua: navigator.userAgent,
      }

      await submit({
        hash,
        tempUserId,
        userContact: data?.number,
        metadata,
      })
    },
    [
      error,
      hash,
      image,
      processing,
      shopKey,
      submit,
      submitError,
      submitted,
      tempUserId,
      url,
      urlFromHash,
      urlParam,
    ]
  )

  const reloadPage = () => {
    setReloading(true)
    analytics.track(AnalyticsEventName.ResultsErrorReload, {
      hash,
      url,
      urlParam,
      urlFromHash,
      shopKey,
      resultsData,
      tempUserId,
      error,
    })

    setTimeout(() => {
      window.location.reload()
    }, 500)
  }

  useEffect(() => {
    if (!router.isReady) return
    if (eventSent) return

    if (error) {
      analytics.track(AnalyticsEventName.ResultsErrorShown, {
        hash,
        url,
        urlParam,
        urlFromHash,
        shopKey,
        resultsData,
        tempUserId,
        error,
      })
      setEventSent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSent, error, router.isReady])

  const canShowCTA =
    (isMobile && isIOS) || isChrome || isChromium || isFirefox || isSafari
  const isChromeIcon = isChrome || isChromium

  return (
    <div className='w-full space-y-8 py-8'>
      <div className='mx-auto flex max-w-sm flex-col items-center space-y-6 px-4 text-center'>
        <LottiePlayer
          src='https://lottie.host/b8dd671a-be70-4d9c-801c-93b54cc8fd5a/rATEr3c9ql.lottie'
          loop
          autoplay
          style={{
            height: 100,
            width: 100,
          }}
        />

        <div className='space-y-2'>
          <h2 className='max-w-md text-center text-4xl font-black'>
            Too many people
            <br />
            are duping
          </h2>

          <div className='relative text-lg'>
            Try reloading the page in a sec
            <CurvedArrow className='absolute -bottom-3 right-[-30px]' />
          </div>
        </div>

        <div className='relative w-full space-y-3 pt-2'>
          <Button
            variant='secondary'
            size='lg'
            className='w-full'
            onClick={reloadPage}
            disabled={reloading}
          >
            <ReloadIcon
              className={clsx('mr-2', {
                'animate-spin': reloading,
              })}
              height={18}
            />
            <span>Try Again</span>
          </Button>

          <Link href='/' className='block w-full'>
            <Button size='lg' className='w-full'>
              Go Home
            </Button>
          </Link>
        </div>

        {canShowCTA && (
          <>
            <div className='flex w-full items-center'>
              <div className='flex-grow border-t border-gray-200'></div>
              <span className='mx-4 flex-shrink text-sm text-gray-400'>or</span>
              <div className='flex-grow border-t border-gray-200'></div>
            </div>

            {/* Primary CTA - Extension or App */}
            <p className='font-medium md:text-lg'>
              Get <span className='font-bold'>instant</span> results with our{' '}
              {isMobile ? (
                'mobile app'
              ) : (
                <>
                  <br />
                  browser extension
                </>
              )}
            </p>
          </>
        )}

        {isMobile && isIOS ? (
          <Link
            href={`${APP_STORE_URL}?pt=124011140&mt=8&ct=ErrorPageCTA`}
            target='_blank'
            className='w-full'
          >
            <Button
              variant='custom'
              size='lg'
              className='bg-bellblue flex w-full items-center justify-center space-x-3 border-2 border-black text-black transition-colors hover:bg-[#9DEEFF]'
            >
              <AppleIcon width={24} height={24} className='text-black' />
              <span>Download iOS App</span>
            </Button>
          </Link>
        ) : (
          !isMobile &&
          (isChrome || isChromium || isFirefox || isSafari) && (
            <Link
              href={
                isFirefox
                  ? 'https://addons.mozilla.org/en-US/firefox/addon/dupe/'
                  : isSafari
                    ? 'https://testflight.apple.com/join/t42zT9sb'
                    : 'https://chromewebstore.google.com/detail/dupe/pkhpcfoaocmmpmphhenoblggeinedlah'
              }
              target='_blank'
              className='w-full'
            >
              <Button
                variant='custom'
                size='lg'
                className='bg-bellblue flex w-full items-center justify-center space-x-3 border-2 border-black text-black transition-colors hover:bg-[#9DEEFF]'
              >
                <Image
                  src={
                    isFirefox
                      ? '/assets/apps/firefox.png'
                      : isSafari
                        ? '/assets/apps/safari.png'
                        : '/assets/apps/chromium.png'
                  }
                  alt=''
                  width={isChromeIcon ? 113 : 24}
                  height={isChromeIcon ? 24 : 24}
                  className='object-contain'
                />
                <span>Get the Extension</span>
              </Button>
            </Link>
          )
        )}
      </div>

      <div className='mx-auto w-full max-w-sm space-y-4 px-4 pt-10'>
        <div className='text-center'>
          <p className='font-medium'>
            Or let us text you when this issue is fixed
          </p>
        </div>
        <PhoneCaptureForm onSubmitCallback={submitIssue} reason='issue' />
      </div>
    </div>
  )
}

export default ResultsErrorForm
